import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { MaskedLines, Reveal } from "../components/Motion";
import { IMAGES, SITE, STATS } from "../lib/data";

const chapters = [
  {
    n: "01",
    title: "The mandate",
    body:
      "I lead real estate marketing and sales strategy for a portfolio of residential, plotted and farmhouse developments across the Noida Expressway corridor, Uttar Pradesh, and North Goa. My work begins where a project meets its market.",
  },
  {
    n: "02",
    title: "The team",
    body:
      "Overseeing a team of nearly 200 sales professionals, I build the systems, tools and narratives that let us reach the right buyers quickly — and answer every question on RERA, pricing and possession before it is asked.",
  },
  {
    n: "03",
    title: "The focus",
    body:
      "Villa plots, farmhouse communities and plotted developments positioned around major infrastructure growth — including the upcoming Noida International Airport at Jewar — and freehold plots in North Goa.",
  },
  {
    n: "04",
    title: "The principle",
    body:
      "Transparency is the product. Separate line items, honest possession timelines and verifiable RERA numbers. Trust compounds faster than any marketing spend.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 md:pt-44">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <p className="overline text-[#C19A6B] mb-6">About</p>
        <h1 className="font-serif-lux text-[#1A1A1A] text-[13vw] md:text-[8vw] leading-[0.9] tracking-tight">
          <MaskedLines lines={["A marketer's", "eye. A closer's", "discipline."]} />
        </h1>
      </section>

      {/* Portrait + intro */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-center">
        <Reveal className="md:col-span-5">
          <div className="relative">
            <div className="img-zoom-wrap border border-[#E5DFD3] relative grain">
              <img
                src={IMAGES.portrait}
                alt="Sambhav K. Pandey speaking at an event"
                className="img-zoom w-full aspect-[4/5] object-cover object-top"
              />
            </div>
            <div className="hidden md:block img-zoom-wrap absolute -bottom-10 -right-8 w-44 border-[6px] border-[#FDFBF7] shadow-2xl">
              <img
                src={IMAGES.portrait2}
                alt="Sambhav K. Pandey addressing the team"
                className="img-zoom w-full aspect-[3/4] object-cover object-top"
              />
            </div>
          </div>
        </Reveal>
        <div className="md:col-span-7 md:pl-8">
          <Reveal delay={0.1}>
            <p className="font-serif-lux text-3xl md:text-4xl leading-[1.2] text-[#1A1A1A] tracking-tight">
              I'm {SITE.name} — a real estate marketing consultant based in Noida, working directly with developers to bring their projects to market.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-sans-lux text-[#5C5C5C] mt-8 leading-relaxed max-w-xl">
              My focus spans villa plots, farmhouse communities and plotted developments positioned around the region's biggest infrastructure story — the upcoming Noida International Airport at Jewar — alongside freehold plots in North Goa. From strategy to the field, I own the full journey.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="inline-flex items-center gap-3 mt-10 border border-[#1A1A1A] text-[#1A1A1A] hover:text-[#FDFBF7] btn-lux px-8 py-4 overline"
            >
              Work with me <ArrowUpRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-[#1A1A1A] text-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-2 md:grid-cols-4 gap-y-12">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <p className="font-serif-lux text-6xl md:text-7xl text-[#C19A6B] tracking-tight">{s.value}</p>
              <p className="font-sans-lux text-sm text-[#E5DFD3] mt-2">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Manifesto chapters */}
      <section data-testid="manifesto" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
        <Reveal>
          <p className="overline text-[#C19A6B] mb-16">The approach — in four chapters</p>
        </Reveal>
        <div className="space-y-4">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-6 py-10 border-t border-[#E5DFD3] group">
                <div className="md:col-span-3">
                  <span className="font-serif-lux text-7xl md:text-8xl text-[#E5DFD3] group-hover:text-[#C19A6B] transition-colors duration-500">
                    {c.n}
                  </span>
                </div>
                <h3 className="md:col-span-4 font-serif-lux text-3xl md:text-4xl text-[#1A1A1A] tracking-tight self-center">
                  {c.title}
                </h3>
                <p className="md:col-span-5 font-sans-lux text-[#5C5C5C] leading-relaxed self-center">
                  {c.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
